import { type FormEvent, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  type PackagesAutocompleteResult,
  PackageType,
  usePackageSearch as useCorePackageSearch,
} from "@b2bportal/core-packages";
import type { PackageSearchProps } from "@b2bportal/core-packages/overridable";
import type { InputErrorType } from "@b2bportal/core-types";
import {
  createQueryParams,
  ensureExhaustive,
  getPackageRoutes,
} from "@b2bportal/core-utilities";
import type { Travelers } from "@b2bportal/packages-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { PACKAGES_QUERY_PARAM } from "@tenant/packages";
import dayjs from "dayjs";
import { PackageSearchSelectors } from "../../../../store/packages/selectors";
import { PackagesSearchActions } from "../../../../store/packages/slice/packagesSearch";
import { MINIMUM_DRIVER_AGE, DEFAULT_DRIVER_AGE } from "../constants";

type FormErrors = {
  origin?: InputErrorType;
  destination?: InputErrorType;
  dateRange?: InputErrorType;
};

export const usePackageSearch = (props: PackageSearchProps) => {
  const { context, handlers } = useCorePackageSearch(props);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useI18nContext();

  const { isFormValid, dateRange } = context;
  const [formError, setFormError] = useState<FormErrors>({});

  const origin = useSelector(PackageSearchSelectors.getSearchFormOrigin);
  const destination = useSelector(
    PackageSearchSelectors.getSearchFormDestination
  );
  const guests = useSelector(PackageSearchSelectors.getSearchFormGuests);

  const roomCount = useSelector(PackageSearchSelectors.getSearchFormRoomCount);

  const packageType = useSelector(
    PackageSearchSelectors.getSearchFormPackageType
  );

  const driverAge = useSelector(PackageSearchSelectors.getSearchFormDriverAge);

  const handlePackageTypeChange = (newPackageType: PackageType) => {
    dispatch(PackagesSearchActions.setNewPackageType(newPackageType));
    // For HOTEL_CAR, origin and destination should always be the same
    // When switching to HOTEL_CAR, sync destination with origin
    if (newPackageType === PackageType.HOTEL_CAR && origin != null) {
      dispatch(PackagesSearchActions.setNewDestination(origin));
    }
    // Reset infant counts when switching to a package type without a flight —
    // infants are flight-only pax types and leaving stale counts would inflate
    // the traveller total shown on the non-flight picker.
    if (newPackageType === PackageType.HOTEL_CAR) {
      dispatch(
        PackagesSearchActions.setNewGuests({
          ...guests,
          infantOnLapAges: [],
          infantInSeatAges: [],
        })
      );
    }
  };

  const handleOriginChange = (origin: PackagesAutocompleteResult) => {
    dispatch(PackagesSearchActions.setNewOrigin(origin));
    setFormError((prev) => ({ ...prev, origin: undefined }));
    // For HOTEL_CAR, origin and destination should always be the same
    if (packageType === PackageType.HOTEL_CAR) {
      dispatch(PackagesSearchActions.setNewDestination(origin));
      setFormError((prev) => ({ ...prev, destination: undefined }));
    }
  };

  const handleDestinationChange = (destination: PackagesAutocompleteResult) => {
    dispatch(PackagesSearchActions.setNewDestination(destination));
    setFormError((prev) => ({ ...prev, destination: undefined }));
  };

  const handleOriginFocus = useCallback(() => {
    setFormError((prev) => ({ ...prev, origin: undefined }));
  }, []);

  const handleDestinationFocus = useCallback(() => {
    setFormError((prev) => ({ ...prev, destination: undefined }));
  }, []);

  const handleDateRangeFromChange = useCallback(
    (date: string | undefined) => {
      handlers.handleDateRangeFromChange(date);
      setFormError((prev) => ({ ...prev, dateRange: undefined }));
    },
    [handlers]
  );

  const handleDateRangeUntilChange = useCallback(
    (date: string | undefined) => {
      handlers.handleDateRangeUntilChange(date);
      setFormError((prev) => ({ ...prev, dateRange: undefined }));
    },
    [handlers]
  );

  const packageTypeIncludesFlights = packageType !== PackageType.HOTEL_CAR;

  const handleGuestsChange = useCallback(
    (newGuests: Travelers) => {
      dispatch(PackagesSearchActions.setNewGuests(newGuests));
    },
    [dispatch]
  );

  const handleDriverAgeChange = (driverAge: string | undefined) => {
    if (driverAge == null || driverAge === "") {
      // Clear the driver age when field is empty
      dispatch(PackagesSearchActions.setDriverAge(undefined));
    } else {
      const age = Number(driverAge);
      // Only accept valid integers within reasonable age range (0-120)
      // Invalid input is silently ignored, preserving previous valid value
      if (!isNaN(age) && Number.isInteger(age) && age >= 0 && age <= 120) {
        dispatch(PackagesSearchActions.setDriverAge(age));
      }
    }
  };

  const handleSearch = useCallback(() => {
    // For HOTEL_CAR, use origin as destination when destination is not provided
    const effectiveDestination =
      packageType === PackageType.HOTEL_CAR && destination == null
        ? origin
        : destination;

    if (
      origin != null &&
      effectiveDestination != null &&
      dateRange?.until != null &&
      dateRange?.from != null
    ) {
      const originLabel = origin.label;
      const destinationLabel = effectiveDestination.label;

      // For HOTEL_CAR packages, both origin and destination will be the same value.
      // This is intentional as the user only selects one location for stay+car packages.
      const searchParams = createQueryParams({
        [PACKAGES_QUERY_PARAM.destination]:
          encodeURIComponent(destinationLabel),
        [PACKAGES_QUERY_PARAM.origin]: encodeURIComponent(originLabel),
        [PACKAGES_QUERY_PARAM.fromDate]: dayjs(dateRange.from).toDate(),
        [PACKAGES_QUERY_PARAM.untilDate]: dayjs(dateRange.until).toDate(),
        [PACKAGES_QUERY_PARAM.adults]: guests.adultCount,
        [PACKAGES_QUERY_PARAM.children]: guests.childAges,
        [PACKAGES_QUERY_PARAM.packageType]: packageType,
        [PACKAGES_QUERY_PARAM.driverAge]:
          driverAge != null && driverAge >= MINIMUM_DRIVER_AGE
            ? driverAge
            : DEFAULT_DRIVER_AGE,
        [PACKAGES_QUERY_PARAM.roomsCount]: roomCount,
      });
      if (guests.infantOnLapAges.length) {
        searchParams.set(
          PACKAGES_QUERY_PARAM.infantsOnLap,
          String(guests.infantOnLapAges.length)
        );
      }
      if (guests.infantInSeatAges.length) {
        searchParams.set(
          PACKAGES_QUERY_PARAM.infantsInSeat,
          String(guests.infantInSeatAges.length)
        );
      }
      switch (packageType) {
        case PackageType.HOTEL_FLIGHT_CAR:
        case PackageType.HOTEL_CAR:
        case PackageType.HOTEL_FLIGHT:
          navigate(
            `${getPackageRoutes().FULL_ROUTES.HOTELS.AVAILABILITY}?${searchParams.toString()}`
          );
          break;
        case PackageType.FLIGHT_CAR:
          navigate(
            `${getPackageRoutes().FULL_ROUTES.FLIGHTS.DEPARTURE}?${searchParams.toString()}`
          );
          break;
        default:
          ensureExhaustive(packageType);
      }
    }
  }, [
    dateRange.from,
    dateRange.until,
    destination,
    driverAge,
    guests.adultCount,
    guests.childAges,
    guests.infantOnLapAges,
    guests.infantInSeatAges,
    navigate,
    origin,
    packageType,
    roomCount,
  ]);

  const shouldShowDriverAge = useMemo(() => {
    // All PackageType's include cars except for hotel + flight
    return packageType !== PackageType.HOTEL_FLIGHT;
  }, [packageType]);

  const shouldShowDestinationSelection = useMemo(() => {
    // We don't need to show the destination selection for hotel + car
    return packageType !== PackageType.HOTEL_CAR;
  }, [packageType]);

  // Override form validation for HOTEL_CAR - destination is not required
  const isFormValidOverride = useMemo(() => {
    // Check if driver age is valid:
    // - If package doesn't show driver age field, it's valid
    // - If it shows driver age, must have a value and meet minimum age
    const isDriverAgeValid = shouldShowDriverAge
      ? driverAge != null && driverAge >= MINIMUM_DRIVER_AGE
      : true;

    if (packageType === PackageType.HOTEL_CAR) {
      // For HOTEL_CAR, we only need origin (destination will be set to origin)
      return (
        origin != null &&
        dateRange?.from != null &&
        dateRange?.until != null &&
        guests?.adultCount != null &&
        isDriverAgeValid
      );
    }
    // For all other package types, use the core validation plus driver age validation
    return isFormValid && isDriverAgeValid;
  }, [
    packageType,
    origin,
    dateRange,
    guests,
    isFormValid,
    driverAge,
    shouldShowDriverAge,
  ]);

  // Override origin label for HOTEL_CAR - use "Where to" semantics
  const originLabel = useMemo(() => {
    // For HOTEL_CAR, the origin field represents the destination, so use destination label
    return packageType === PackageType.HOTEL_CAR
      ? context.destinationLabel
      : context.originLabel;
  }, [packageType, context.destinationLabel, context.originLabel]);

  const onFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      // Reuse the same validation logic as isFormValidOverride
      if (!isFormValidOverride) {
        // Set errors on missing fields
        const errors: FormErrors = {};

        if (!origin) {
          errors.origin = { message: undefined };
        }
        // For package types that show destination, validate it
        if (shouldShowDestinationSelection && !destination) {
          errors.destination = { message: undefined };
        }
        if (!dateRange?.from || !dateRange?.until) {
          errors.dateRange = { message: undefined };
        }

        setFormError(errors);
        return;
      }

      // All validations passed - execute the search
      props.onSubmit ? props.onSubmit() : handleSearch();
      props.onSubmitAppend?.();
    },
    [
      isFormValidOverride,
      props,
      handleSearch,
      origin,
      shouldShowDestinationSelection,
      destination,
      dateRange?.from,
      dateRange?.until,
    ]
  );

  return {
    context: {
      ...context,
      isFormValid: isFormValidOverride,
      originLabel,
      packageType,
      packageTypeIncludesFlights,
      guests,
      driverAge,
      shouldShowDriverAge,
      shouldShowDestinationSelection,
      shouldShowPackageType: true,
      driverAgeLabel: t("core-cars.search.driverAge"),
      driverAgePlaceholder: t("core-packages.search.placeholders.driverAge"),
      originPlaceholder: t("core-packages.search.placeholders.origin"),
      destinationPlaceholder: t(
        "core-packages.search.placeholders.destination"
      ),
      searchButtonLabel: t("core-packages.search.button"),
      formError,
      origin,
      destination,
    },
    handlers: {
      ...handlers,
      handleOriginChange,
      handleDestinationChange,
      handleOriginFocus,
      handleDestinationFocus,
      handleDateRangeFromChange,
      handleDateRangeUntilChange,
      handlePackageTypeChange,
      handleDriverAgeChange,
      handleGuestsChange,
      handleSearch,
      onFormSubmit,
    },
  };
};
